import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { Button, Card, DataTable, ErrorBanner, Field, Input, Select, Spinner, StatTile } from '../components/ui';
import WaterfallCurve from '../components/charts/WaterfallCurve';
import { enumLabel, money, percent, pricePerShare, shares } from '../lib/format';

/**
 * Exit modelling: one exit value in detail, plus a curve across a range.
 *
 * The curve is where the insight is — the kinks show exactly where a preferred
 * class stops taking its liquidation preference and converts instead.
 */
export default function Waterfall() {
  const { companyId } = useCompany();
  const [exit, setExit] = useState('20000000');
  const [includeUnvested, setIncludeUnvested] = useState('false');
  const [detail, setDetail] = useState(null);
  const [curve, setCurve] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  async function run(event) {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const params = { exitProceeds: exit, includeUnvested };
      const [d, c] = await Promise.all([
        api.waterfall(companyId, params),
        api.waterfallCurve(companyId, {
          exitValues: curvePoints(exit),
          includeUnvested: includeUnvested === 'true',
        }),
      ]);
      setDetail(d);
      setCurve(c);
      setLastParams(params);
    } catch (err) {
      setError(err);
      setDetail(null);
      setCurve(null);
    } finally {
      setBusy(false);
    }
  }

  // Re-downloads the exact scenario shown, not whatever is currently typed
  // into the form -- lastParams is only ever set alongside a result.
  async function download() {
    setDownloading(true);
    setError(null);
    try {
      await api.exportWaterfall(companyId, lastParams);
    } catch (err) {
      setError(err);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Exit waterfall</h1>
          <p>
            Liquidation preferences paid in seniority order, then the convert-or-take-preference
            election solved to a fixed point, participation caps applied, and in-the-money
            options exercised.
          </p>
        </div>
      </div>

      <Card>
        <form onSubmit={run} style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Exit proceeds">
            <Input value={exit} onChange={(e) => setExit(e.target.value)} inputMode="decimal" style={{ width: 200 }} />
          </Field>
          <Field label="Unvested awards">
            <Select value={includeUnvested} onChange={(e) => setIncludeUnvested(e.target.value)}>
              <option value="false">Vested only</option>
              <option value="true">Assume acceleration</option>
            </Select>
          </Field>
          <Button type="submit" variant="primary" loading={busy}>Model exit</Button>
        </form>
      </Card>

      <ErrorBanner error={error} />
      {busy && !detail ? <Spinner label="Modelling" /> : null}

      {detail ? (
        <>
          <div className="grid grid-stats">
            <StatTile label="Exit proceeds" value={money(detail.exitProceeds, { places: 0 })} />
            <StatTile label="Implied price/share" value={pricePerShare(detail.impliedPricePerShare)} />
            <StatTile label="Preferences paid" value={money(detail.totals.preferencesPaid, { places: 0 })} />
            <StatTile label="Option strike proceeds" value={money(detail.totals.optionStrikeProceeds, { places: 0 })} />
            <StatTile label="Participating shares" value={shares(detail.totals.participatingShares)} />
          </div>

          <div>
            <Button loading={downloading} onClick={download}>Download as spreadsheet</Button>
          </div>

          {curve?.length ? (
            <Card title="Proceeds across exit values">
              <WaterfallCurve points={curve} />
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                Each bend is an election flipping — where converting starts to beat taking the
                preference.
              </p>
            </Card>
          ) : null}

          <Card title="By share class" padded={false}>
            <DataTable
              rowKey={(r) => r.shareClassId}
              empty="No preferred classes."
              columns={[
                { key: 'name', header: 'Class', render: (r) => <span className="primary-cell">{r.name}</span> },
                { key: 'seniorityRank', header: 'Seniority', align: 'right', numeric: true },
                { key: 'shares', header: 'Shares', align: 'right', numeric: true, render: (r) => shares(r.shares) },
                { key: 'elected', header: 'Election', render: (r) => (r.electedConversion ? 'Converted to common' : 'Took preference') },
                { key: 'pref', header: 'Preference', align: 'right', numeric: true, render: (r) => money(r.preferencePaid, { places: 0 }) },
                { key: 'part', header: 'Participation', align: 'right', numeric: true, render: (r) => money(r.participationPaid, { places: 0 }) },
                { key: 'conv', header: 'As converted', align: 'right', numeric: true, render: (r) => money(r.asConvertedPaid, { places: 0 }) },
                { key: 'capped', header: '', render: (r) => (r.cappedOut ? 'capped out' : '') },
              ]}
              rows={detail.shareClasses}
            />
          </Card>

          <Card title="By stakeholder" padded={false}>
            <DataTable
              rowKey={(r) => r.stakeholderId}
              columns={[
                { key: 'name', header: 'Stakeholder', render: (r) => <span className="primary-cell">{r.name}</span> },
                { key: 'total', header: 'Proceeds', align: 'right', numeric: true, render: (r) => money(r.total, { places: 0 }) },
                { key: 'pct', header: '% of exit', align: 'right', numeric: true, render: (r) => percent(r.percentOfProceeds) },
                {
                  key: 'lines',
                  header: 'Made up of',
                  render: (r) => (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {r.lines
                        .map((l) => `${enumLabel(l.kind)}${l.shareClass ? ` (${l.shareClass})` : ''}`)
                        .join(', ')}
                    </span>
                  ),
                },
              ]}
              rows={detail.stakeholders}
            />
          </Card>

          <div className="banner banner-warning">
            <div>
              <strong>What this does not model</strong>
              <ul className="banner-issues">
                {detail.caveats.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** A spread of exit values around the chosen one, for the curve. */
function curvePoints(exit) {
  const base = Number(String(exit).split('.')[0]) || 0;
  if (base <= 0) return ['0'];
  const top = base * 2;
  const step = top / 11;
  return Array.from({ length: 12 }, (_, i) => String(Math.round(i * step)));
}
